import type { Editor } from "@tiptap/react";
import { ReactNodeViewRenderer } from "@tiptap/react";
import {
  CustomMentionExtensionConfig,
  type MentionExtensionOptions
} from "shared/components/Editor/core/extentions/mentions/extention-config";
import { MentionComponentAttributeNames } from "shared/components/Editor/core/extentions/mentions/types";
import { parseVariableToken } from "views/settings-communication/lib/variable-token";
import type { ContactUnitLinks } from "../api/crm";
import { InboxVariableChip } from "./InboxVariableChip";
import {
  ATTR_LINK_TYPE,
  ATTR_UNIT_ID,
  MENTION_ENTITY_NAME,
  isLinkVariableKey,
  linkForType,
  syncUnitScopedValueChips,
  type LinkType
} from "./link-variable-shared";

export { isLinkVariableKey, type LinkType };

// eslint-disable-next-line sonarjs/slow-regex -- safe: [^}]+ cannot backtrack across }}
const TEMPLATE_VAR_REGEX = /\{\{([^}]+)\}\}/g;

export const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// --- Extension factory ---

// Extends the base mention node with linkType/unitId attrs and a node view
// that branches between the existing name chip and the new link chip.
// disableMentions on LiteTextEditor still removes the @-mention plugin; this
// only renders chips (no suggestion popup). Variable/unit data intentionally
// lives OUTSIDE the extension (InboxVariableDataContext): the editor
// instantiates extensions once, so options would freeze at mount-time values.
export const createInboxVariableExtension = () =>
  CustomMentionExtensionConfig.extend({
    addOptions(this: { parent?: () => Partial<MentionExtensionOptions> }) {
      return {
        ...this.parent?.(),
        renderComponent: () => null,
        getMentionedEntityDetails: () => undefined
      } as MentionExtensionOptions;
    },
    addAttributes() {
      return {
        ...this.parent?.(),
        [ATTR_LINK_TYPE]: {
          default: null,
          parseHTML: (element: HTMLElement) =>
            element.getAttribute("data-link-type"),
          renderHTML: (chipAttrs: Record<string, unknown>) =>
            chipAttrs[ATTR_LINK_TYPE]
              ? { "data-link-type": chipAttrs[ATTR_LINK_TYPE] }
              : {}
        },
        [ATTR_UNIT_ID]: {
          default: null,
          parseHTML: (element: HTMLElement) => {
            const raw = element.getAttribute("data-unit-id");
            return raw ? Number(raw) : null;
          },
          renderHTML: (chipAttrs: Record<string, unknown>) =>
            chipAttrs[ATTR_UNIT_ID] != null
              ? { "data-unit-id": String(chipAttrs[ATTR_UNIT_ID]) }
              : {}
        }
      };
    },
    renderText({ node }) {
      // Both name and link chips serialise to their `title`. For link chips
      // the title is the chosen unit's literal short URL (empty if unset).
      return (node.attrs as Record<string, string>).title ?? "";
    },
    addNodeView() {
      return ReactNodeViewRenderer(InboxVariableChip);
    }
  }).configure({
    suggestion: {
      allow: () => false
    }
  });

// --- Template seeding ---

// Build editor HTML from a rendered SMS template. Name variables become a
// chip whose title is the resolved value (as before). Bare link tokens
// become an UNRESOLVED link chip (no unit) that blocks send until the user
// picks a unit or removes it.
export const buildInboxEditorHtml = (
  rawBody: string,
  variableValues: Record<string, string>
): string => {
  const parts: string[] = [];
  let lastIndex = 0;

  for (const match of rawBody.matchAll(TEMPLATE_VAR_REGEX)) {
    if (match.index > lastIndex)
      parts.push(escapeHtml(rawBody.slice(lastIndex, match.index)));

    const { key, fallback } = parseVariableToken(match[1]);
    if (isLinkVariableKey(key))
      parts.push(
        `<mention-component id="${escapeHtml(key)}" entity_identifier="${escapeHtml(key)}" entity_name="${MENTION_ENTITY_NAME}" data-link-type="${escapeHtml(key)}" title=""></mention-component>`
      );
    else {
      const value = variableValues[key] || fallback || "";
      parts.push(
        `<mention-component id="${escapeHtml(key)}" entity_identifier="${escapeHtml(key)}" entity_name="${MENTION_ENTITY_NAME}" title="${escapeHtml(value)}"></mention-component>`
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < rawBody.length)
    parts.push(escapeHtml(rawBody.slice(lastIndex)));

  return parts.length > 0 ? `<p>${parts.join("")}</p>` : "";
};

// HTML for a single link-variable chip to insert at the caret. When a unit
// is pre-selected we resolve its short URL into the title immediately.
export const buildLinkChipHtml = (
  key: LinkType,
  unit?: ContactUnitLinks | null
): string => {
  const link = unit ? linkForType(unit, key) : null;
  const unitAttr = unit ? ` data-unit-id="${unit.unit_id}"` : "";
  return `<mention-component id="${escapeHtml(key)}" entity_identifier="${escapeHtml(key)}" entity_name="${MENTION_ENTITY_NAME}" data-link-type="${escapeHtml(key)}"${unitAttr} title="${escapeHtml(link?.short_url ?? "")}"></mention-component>`;
};

// --- Gating ---

interface InboxDocNode {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: InboxDocNode[];
}

// A link chip is unresolved when it has a linkType but no chosen unit, or
// the chosen unit has no link of that type (empty title). Walks the Tiptap
// JSON doc so it works for both the SMS composer and any chip-based body.
export const docHasUnresolvedLinkChip = (
  doc: InboxDocNode | null | undefined
): boolean => {
  if (!doc) return false;
  const attrs = doc.attrs ?? {};
  if (
    doc.type === "mention" &&
    attrs[ATTR_LINK_TYPE] &&
    (attrs[ATTR_UNIT_ID] == null ||
      !(attrs[MentionComponentAttributeNames.Title] as string | undefined))
  )
    return true;
  return (doc.content ?? []).some(docHasUnresolvedLinkChip);
};

// Collects the unit ids of every resolved link chip in the Tiptap JSON doc.
// The composer maps these to a lead (via singleLeadForUnits) so the send binds
// to the lead whose unit the link points at. Walks the same doc shape as
// docHasUnresolvedLinkChip.
export const collectDocUnitIds = (
  doc: InboxDocNode | null | undefined
): number[] => {
  if (!doc) return [];
  const attrs = doc.attrs ?? {};
  const own =
    doc.type === "mention" &&
    attrs[ATTR_LINK_TYPE] &&
    typeof attrs[ATTR_UNIT_ID] === "number"
      ? [attrs[ATTR_UNIT_ID] as number]
      : [];
  return own.concat((doc.content ?? []).flatMap(collectDocUnitIds));
};

export const nextLinkUnitIds = (previous: number[], doc: unknown): number[] => {
  const next = collectDocUnitIds(doc as InboxDocNode | null | undefined);
  const unchanged =
    next.length === previous.length &&
    next.every((unitId, index) => unitId === previous[index]);
  return unchanged ? previous : next;
};

// Pre-select a unit on every still-unresolved link chip in the editor. Used
// after applying a template (which seeds bare link tokens as unresolved
// chips) when the contact has exactly one unit, so the user does not have to
// pick the only option manually. Also syncs any unit-scoped value chip
// (lead.property_name, lead.property_address, lead.listing_link) to this
// unit's real value — a single unit for the contact is itself an
// unambiguous signal, independent of whether the template has a link token.
export const resolveUnresolvedLinkChips = (
  editor: Editor,
  unit: ContactUnitLinks
): void => {
  const updates: { pos: number; attrs: Record<string, unknown> }[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== "mention") return;
    const attrs = node.attrs as Record<string, unknown>;
    const linkType = attrs[ATTR_LINK_TYPE] as LinkType | null;
    if (!linkType || attrs[ATTR_UNIT_ID] != null) return;
    const link = linkForType(unit, linkType);
    if (!link) return;
    updates.push({
      pos,
      attrs: {
        ...attrs,
        [ATTR_UNIT_ID]: unit.unit_id,
        [MentionComponentAttributeNames.Title]: link.short_url
      }
    });
  });
  if (updates.length > 0) {
    const tr = editor.state.tr;
    updates.forEach(({ pos, attrs }) =>
      tr.setNodeMarkup(pos, undefined, attrs)
    );
    editor.view.dispatch(tr);
  }
  syncUnitScopedValueChips(editor, unit);
};

// A chip serialises to <mention-component title="…"></mention-component>, whose
// visible text lives in an attribute. Mail clients render an empty custom
// element as nothing, so outgoing HTML has to carry the resolved text instead.
export const flattenVariableChips = (html: string): string =>
  html.replace(
    /<mention-component\b([^>]*?)(?:\/>|><\/mention-component>)/gi,
    (_tag, attrs: string) => {
      const title = /\btitle="([^"]*)"/i.exec(attrs)?.[1] ?? "";
      if (!title) return "";

      return /\bdata-link-type=/i.test(attrs)
        ? `<a href="${title}">${title}</a>`
        : title;
    }
  );
